import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router";
import {
  ArrowRight,
  BookOpen,
  ChevronRight,
  Clock,
  Facebook,
  Linkedin,
  Loader2,
  Lock,
  Share2,
  Sparkles,
  User,
} from "lucide-react";
import {
  INDUSTRIES,
  INDUSTRY_MENU_CATEGORIES,
  getItemsByIndustry,
} from "../../data/categoryData";
import { useCategories } from "../../contexts/CategoriesContext";
import { getSponsorForIndustry } from "../../data/mockContentData";
import Footer from "../../components/footer/Footer";
import ArticleAuthFlowModal from "../../components/auth/ArticleAuthFlowModal";
import SubcategoryInterestGateModal from "../../components/guest/SubcategoryInterestGateModal";
import { resolveCanonicalTopicSlug, topicSlugsMatch } from "../../utils/topicSlugAliases";
import { pickMenuSlug, resolveTopicBackendIds } from "../../utils/resolveTopicBackendIds";
import { useTopicArticles } from "../../hooks/useTopicArticles";
import { useTopicMedia } from "../../hooks/useTopicMedia";
import TopicLibraryEmptyState from "../../components/guest/TopicLibraryEmptyState";
import { useSubcategoryInterestGate } from "../../hooks/useSubcategoryInterestGate";
import { isTopicContentAccessGranted } from "../../utils/topicAccess";
import showToast from "../../utils/toast";
import {
  articleSlugMatches,
  getSectionNavLabel,
  isSameConsumerArticle,
  splitArticleSectionsForAccess,
} from "../../utils/consumerArticleHelpers";
import { ArticleSectionsList } from "../../components/articles/ArticleSectionRenderer";
import { ExpertProviderCta } from "./subcategory_pages/shared/categoryDetailShared";
import { useTopicProvider } from "../../hooks/useTopicProvider";
import { buildExpertCtaView } from "../../utils/providerDisplay";
import { rowMatchesTopic } from "../../utils/topicContentMatch";

const EMPTY_MENU = [];

const STATIC_MENU_ID_PREFIX = {
  "real-estate": "re",
  legal: "leg",
  nutrition: "nut",
  health: "hlth",
  careers: "car",
  "betting-gaming": "bet",
};

function menuItemFromRegistryEntry(industryId, entry) {
  if (!entry) return null;
  const prefix = STATIC_MENU_ID_PREFIX[industryId] || "sub";
  return {
    id: `${prefix}-${entry.id}`,
    label: entry.name,
    path: `/${industryId}/${entry.slug}`,
    description: entry.description,
    icon: entry.icon,
    color: entry.color,
    slug: entry.slug,
  };
}

function ArticleShareButtons({ shareUrl, title }) {
  const encodedUrl = encodeURIComponent(shareUrl);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast.success("Link copied to clipboard");
    } catch {
      showToast.error("Could not copy link");
    }
  }

  const buttonCls =
    "inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-bold text-gray-600 transition hover:border-black hover:text-black";

  return (
    <div className="flex flex-wrap gap-2">
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonCls}
        aria-label="Share on Facebook"
      >
        <Facebook size={14} aria-hidden />
        Facebook
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonCls}
        aria-label="Share on LinkedIn"
      >
        <Linkedin size={14} aria-hidden />
        LinkedIn
      </a>
      <button type="button" onClick={copyLink} className={buttonCls} aria-label="Copy article link">
        <Share2 size={14} aria-hidden />
        Copy
      </button>
    </div>
  );
}

const RelatedArticleCard = ({ article, industryId, topicSlug, color }) => (
  <Link
    to={`/${industryId}/${topicSlug}/${article.slug}`}
    className="group overflow-hidden rounded-3xl border border-black/[0.08] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
  >
    <div className="relative h-36 overflow-hidden bg-gray-100">
      <img src={article.coverImage} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <span
        className="absolute bottom-3 left-3 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em]"
        style={{ background: color, color: "#111" }}
      >
        {article.type}
      </span>
    </div>
    <div className="p-4">
      <h3 className="line-clamp-2 text-sm font-black leading-snug text-gray-950">{article.title}</h3>
      <p className="mt-2 text-xs font-bold text-gray-400">{article.readTime}</p>
    </div>
  </Link>
);

function ArticlePaywallOverlay({
  isAuthenticated,
  sponsorName,
  topicLabel,
  onSignUp,
  onLogin,
  onAddInterest,
  standalone = false,
}) {
  return (
    <div
      className={
        standalone
          ? "flex justify-center py-8"
          : "absolute inset-x-0 bottom-0 top-8 flex items-end justify-center bg-gradient-to-b from-white/10 via-white/85 to-white pb-6 pt-16 sm:items-center sm:pb-0 sm:pt-0"
      }
    >
      <div className="mx-auto max-w-md rounded-3xl border border-[#FFD221]/35 bg-white p-6 text-center shadow-xl sm:p-7">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFD221] text-black">
          <Lock size={20} />
        </div>
        <h3 className="text-xl font-black text-gray-900">
          {!isAuthenticated
            ? "Register to continue reading"
            : "Add this topic to continue reading"}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-gray-500">
          {!isAuthenticated ? (
            <>
              Unlock the rest of this provider guide and send your interest context to {sponsorName}.
            </>
          ) : (
            <>
              Add <span className="font-semibold text-gray-700">{topicLabel}</span> to your interests to read every
              section and connect with {sponsorName}.
            </>
          )}
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          {!isAuthenticated ? (
            <>
              <button
                type="button"
                onClick={onSignUp}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFD221] px-5 py-3 text-sm font-black text-black"
              >
                Register free
                <ArrowRight size={15} />
              </button>
              <button
                type="button"
                onClick={onLogin}
                className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-5 py-3 text-sm font-bold text-gray-700"
              >
                Log in
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onAddInterest}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFD221] px-5 py-3 text-sm font-black text-black"
            >
              Add topic to my interests
              <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const ArticleDetailPage = () => {
  const { industryId: paramIndustryId, slug: slugParam, articleSlug } = useParams();
  const location = useLocation();
  const { token, user } = useSelector((s) => s.infoboxx_consumer);
  const isAuthenticated = Boolean(token);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authIntent, setAuthIntent] = useState("signup");
  const [whatsappPending, setWhatsappPending] = useState(false);
  const interestGateWasOpenRef = useRef(false);

  const {
    loading: categoriesLoading,
    industries: apiIndustries,
    menuByIndustry,
    signupCategories,
  } = useCategories();

  const industryId = paramIndustryId ?? location.pathname.split("/").filter(Boolean)[0];
  const slug = useMemo(
    () => resolveCanonicalTopicSlug(industryId, slugParam),
    [industryId, slugParam],
  );

  const industries = apiIndustries.length ? apiIndustries : INDUSTRIES;
  const industry = industries.find((candidate) => candidate.id === industryId);
  const apiMenu = useMemo(
    () => menuByIndustry[industryId] ?? EMPTY_MENU,
    [menuByIndustry, industryId],
  );
  const staticMenu = useMemo(
    () => INDUSTRY_MENU_CATEGORIES[industryId] ?? EMPTY_MENU,
    [industryId],
  );

  const menuItem = useMemo(() => {
    const raw = slug ? decodeURIComponent(slug) : "";
    const target = (raw || slug || "").trim();
    if (!target) return null;

    const match = (list) =>
      list.find((c) => {
        const s = pickMenuSlug(c);
        return (
          s === target ||
          s === slug ||
          topicSlugsMatch(s, target) ||
          topicSlugsMatch(s, slugParam ?? "")
        );
      });

    const registryHit = getItemsByIndustry(industryId).find(
      (it) => topicSlugsMatch(it.slug, target) || topicSlugsMatch(it.slug, slugParam ?? ""),
    );

    return (
      match(apiMenu) ??
      match(staticMenu) ??
      (registryHit ? menuItemFromRegistryEntry(industryId, registryHit) : null)
    );
  }, [apiMenu, staticMenu, slug, slugParam, industryId]);

  const topicArticles = useTopicArticles({
    signupCategories,
    industryId,
    menuItem,
    enabled: Boolean(menuItem),
  });

  const topicMedia = useTopicMedia({
    isAuthenticated,
    user,
    signupCategories,
    industryId,
    menuItem,
    enabled: Boolean(menuItem),
  });

  const topicProvider = useTopicProvider({
    isAuthenticated,
    user,
    signupCategories,
    industryId,
    menuItem,
    enabled: Boolean(menuItem),
  });

  const topicIds = useMemo(
    () => resolveTopicBackendIds(signupCategories, industryId, menuItem),
    [signupCategories, industryId, menuItem],
  );

  const article = useMemo(() => {
    const hit =
      topicArticles.articles.find((row) => articleSlugMatches(row.raw, articleSlug)) || null;
    if (!hit) return null;
    if (topicIds && !rowMatchesTopic(hit.raw, topicIds)) return null;
    return hit;
  }, [topicArticles.articles, articleSlug, topicIds]);

  const canReadFull = isTopicContentAccessGranted(isAuthenticated, user, topicIds);
  const topicLabel = menuItem?.label || article?.subCategoryName || "this topic";
  const sponsor = useMemo(() => getSponsorForIndustry(industryId), [industryId]);

  const interestGate = useSubcategoryInterestGate({
    isAuthenticated,
    user,
    signupCategories,
    industryId,
    industryLabel: industry?.label ?? "",
    menuItem,
    slug,
  });
  const { open: interestGateOpen, setOpen: setInterestGateOpen } = interestGate;

  const serviceProviderName = useMemo(
    () =>
      article?.providerName ||
      article?.raw?.service_provider_name ||
      article?.raw?.service_provider?.name ||
      article?.raw?.service_provider?.company_name ||
      null,
    [article],
  );

  const expertCta = useMemo(
    () =>
      buildExpertCtaView({
        provider: topicProvider.provider,
        isAuthenticated,
        fallbackSponsor: sponsor,
        topicLabel,
        articleRow: article?.raw,
      }),
    [topicProvider.provider, isAuthenticated, sponsor, topicLabel, article?.raw],
  );

  const openExpertWhatsapp = useCallback(() => {
    if (!expertCta.whatsappUrl || typeof window === "undefined") return;
    window.open(expertCta.whatsappUrl, "_blank", "noopener,noreferrer");
  }, [expertCta.whatsappUrl]);

  const handleTalkToExpert = useCallback(() => {
    if (!expertCta.whatsappUrl) return;

    if (!isAuthenticated) {
      setWhatsappPending(true);
      setAuthIntent("signup");
      setAuthModalOpen(true);
      return;
    }

    if (isTopicContentAccessGranted(isAuthenticated, user, topicIds)) {
      openExpertWhatsapp();
      return;
    }

    setWhatsappPending(true);
    setInterestGateOpen(true);
  }, [
    expertCta.whatsappUrl,
    isAuthenticated,
    user,
    topicIds,
    openExpertWhatsapp,
    setInterestGateOpen,
  ]);

  useEffect(() => {
    if (!whatsappPending || !isAuthenticated || !user) return;

    if (isTopicContentAccessGranted(isAuthenticated, user, topicIds)) {
      openExpertWhatsapp();
      setWhatsappPending(false);
      return;
    }

    if (!interestGateOpen) {
      setInterestGateOpen(true);
    }
  }, [
    whatsappPending,
    isAuthenticated,
    user,
    topicIds,
    interestGateOpen,
    openExpertWhatsapp,
    setInterestGateOpen,
  ]);

  useEffect(() => {
    const wasOpen = interestGateWasOpenRef.current;
    interestGateWasOpenRef.current = interestGateOpen;

    if (wasOpen && !interestGateOpen && whatsappPending) {
      if (isTopicContentAccessGranted(isAuthenticated, user, topicIds)) {
        openExpertWhatsapp();
      }
      setWhatsappPending(false);
    }
  }, [
    interestGateOpen,
    whatsappPending,
    isAuthenticated,
    user,
    topicIds,
    openExpertWhatsapp,
  ]);

  useEffect(() => {
    if (!authModalOpen && whatsappPending && !isAuthenticated) {
      setWhatsappPending(false);
    }
  }, [authModalOpen, whatsappPending, isAuthenticated]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const scrollNow = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    scrollNow();
    const raf = requestAnimationFrame(scrollNow);
    return () => cancelAnimationFrame(raf);
  }, [industryId, slug, articleSlug]);

  const navSections = useMemo(() => article?.navSections ?? [], [article?.navSections]);
  const { previewSections, lockedSections, showBodyPaywall } = useMemo(
    () => splitArticleSectionsForAccess(navSections, canReadFull),
    [navSections, canReadFull],
  );
  
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return topicArticles.articles
      .filter((item) => !isSameConsumerArticle(item, article, articleSlug))
      .filter((item) => !topicIds || rowMatchesTopic(item.raw, topicIds))
      .slice(0, 3);
  }, [topicArticles.articles, article, articleSlug, topicIds]);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}${location.pathname}${location.search || ""}`;
  }, [location.pathname, location.search]);

  const showMediaSection =
    topicMedia.canAccess && (topicMedia.loading || topicMedia.totalCount > 0);

  const color = menuItem?.color ?? industry?.color ?? "#c9a000";
  const returnPath = `${location.pathname}${location.search || ""}`;
  const userDisplay =
    user?.email ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    null;

  if (categoriesLoading || topicArticles.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f4ec] px-4">
        <div className="flex flex-col items-center gap-3 rounded-3xl bg-white p-10 shadow-xl" role="status">
          <Loader2 className="h-10 w-10 animate-spin" style={{ color }} />
          <p className="text-sm font-semibold text-gray-600">Loading article…</p>
        </div>
      </div>
    );
  }

  if (!industry || !menuItem) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f4ec] px-4">
        <div className="rounded-3xl bg-white p-10 text-center shadow-xl">
          <p className="text-3xl font-black text-gray-900">Topic not found</p>
          <Link to={`/${industryId}`} className="mt-3 inline-block font-bold text-[#c9a000]">
            Browse topics
          </Link>
        </div>
      </div>
    );
  }

  if (!article) {
    const subCategoryLabel = topicArticles.topicIds?.subCategoryName || topicLabel;
    return (
      <div className="min-h-screen bg-[#f7f4ec] px-4 py-28">
        <div className="mx-auto max-w-lg">
          <TopicLibraryEmptyState
            kind="article"
            topicLabel={subCategoryLabel}
            color={color}
            showRetry={Boolean(topicArticles.error)}
            onRetry={topicArticles.reload}
          />
          <div className="mt-6 text-center">
            <Link
              to={`/${industryId}/${slug}`}
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-3 text-sm font-bold text-black shadow-sm"
            >
              Back to {topicLabel}
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f4ec]">
      <section className="relative overflow-hidden bg-black pt-28 text-white">
        <img src={article.coverImage} alt={article.coverAlt} className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/78 to-black" />
        <div className="relative z-10 mx-auto max-w-8xl px-4 pb-14 sm:px-6 lg:px-8">
          <div className="mb-7 flex flex-wrap items-center gap-2 text-xs text-white/55">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight size={12} />
            <Link to={`/${industryId}`} className="hover:text-white">{industry.label}</Link>
            <ChevronRight size={12} />
            <Link to={`/${industryId}/${slug}`} className="hover:text-white">{topicLabel}</Link>
          </div>

          <div className="max-w-4xl">
            <div className="mb-5 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#FFD221] px-4 py-2 text-[11px] font-black uppercase tracking-[0.16em] text-black">
                <Sparkles size={12} />
                {article.badge || article.type}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.16em] text-white backdrop-blur">
                <Clock size={12} />
                {article.readTime}
              </span>
            </div>
            <h1 className="text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {article.title}
            </h1>
            {article.subtitle ? (
              <p className="mt-6 max-w-2xl text-base leading-8 text-white/70 sm:text-lg">{article.subtitle}</p>
            ) : null}
          </div>
        </div>
      </section>

      <main className="mx-auto grid max-w-8xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[260px_1fr_340px] lg:px-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-5">
            <div className="rounded-3xl border border-black/[0.08] bg-white p-5 shadow-sm">
              <p className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-gray-400">In this guide</p>
              {navSections.length ? (
                <nav className="space-y-1" aria-label="Article sections">
                  {navSections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="block rounded-xl px-3 py-2 text-sm font-semibold text-gray-600 transition hover:bg-[#f7f4ec] hover:text-black"
                    >
                      {getSectionNavLabel(section)}
                    </a>
                  ))}
                </nav>
              ) : (
                <p className="text-sm text-gray-400">No sections yet.</p>
              )}
            </div>
            <div className="rounded-3xl border border-black/[0.08] bg-white p-5 shadow-sm">
              <p className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-gray-400">Share</p>
              <ArticleShareButtons shareUrl={shareUrl} title={article.title} />
            </div>
          </div>
        </aside>

        <article className="min-w-0 overflow-hidden rounded-[2rem] border border-black/[0.08] bg-white shadow-sm">
          <div className="border-b border-black/[0.06] p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              {serviceProviderName ? (
                <span className="inline-flex items-center gap-2 font-semibold text-gray-800">
                  <User size={15} style={{ color }} />
                  {serviceProviderName}
                </span>
              ) : null}
              <span className="inline-flex items-center gap-2">
                <BookOpen size={15} style={{ color }} />
                {article.subCategoryName || topicLabel}
              </span>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {previewSections.length > 0 ? (
              <ArticleSectionsList sections={previewSections} color={color} />
            ) : null}

            {!canReadFull && (lockedSections.length > 0 || showBodyPaywall) ? (
              <div className={`relative ${previewSections.length > 0 ? "mt-10" : ""}`}>
                {lockedSections.length > 0 ? (
                  <div className="select-none blur-[5px]" aria-hidden="true">
                    <ArticleSectionsList sections={lockedSections} color={color} />
                  </div>
                ) : null}
                <ArticlePaywallOverlay
                  isAuthenticated={isAuthenticated}
                  sponsorName={sponsor.name}
                  topicLabel={topicLabel}
                  standalone={showBodyPaywall && lockedSections.length === 0}
                  onSignUp={() => {
                    setAuthIntent("signup");
                    setAuthModalOpen(true);
                  }}
                  onLogin={() => {
                    setAuthIntent("login");
                    setAuthModalOpen(true);
                  }}
                  onAddInterest={() => setInterestGateOpen(true)}
                />
              </div>
            ) : null}

            {canReadFull ? (
              <div className="mt-8 rounded-2xl border border-[#FFD221]/40 bg-[#FFD221]/10 p-5">
                <p className="font-black text-gray-900">You have full access</p>
                <p className="mt-1 text-sm text-gray-600">
                  {userDisplay ? (
                    <>
                      Signed in as <span className="font-semibold text-gray-800">{userDisplay}</span>.{" "}
                    </>
                  ) : null}
                  You can read every section in this guide for {topicLabel}.
                </p>
              </div>
            ) : null}
          </div>
        </article>

        <aside className="space-y-5">
          <div className="sticky top-24 space-y-5">
            <ExpertProviderCta
              whatsappUrl={expertCta.whatsappUrl}
              trustLine={expertCta.trustLine}
              responseTime={sponsor.responseTime}
              onTalkToExpertClick={handleTalkToExpert}
            />
          </div>
        </aside>
      </main>

      {navSections.length > 0 ? (
        <div className="mx-auto max-w-8xl px-4 pb-4 lg:hidden">
          <div className="rounded-2xl border border-black/[0.08] bg-white p-4 shadow-sm">
            <p className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-gray-400">In this guide</p>
            <div className="flex flex-wrap gap-2">
              {navSections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="rounded-full border border-black/10 px-3 py-1.5 text-xs font-bold text-gray-600"
                >
                  {getSectionNavLabel(section)}
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {relatedArticles.length > 0 ? (
        <section className="mx-auto max-w-8xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#c9a000]">
                Related reading
              </p>
              <h2 className="mt-2 text-3xl font-black text-gray-950">
                Continue exploring {topicLabel}
              </h2>
            </div>
            <Link
              to={`/${industryId}/${slug}`}
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-3 text-sm font-bold text-black"
            >
              Back to provider hub
              <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {relatedArticles.map((item) => (
              <RelatedArticleCard
                key={`${item.slug}-${item.providerSlug || item.providerId || item.title}`}
                article={item}
                industryId={industryId}
                topicSlug={slug}
                color={color}
              />
            ))}
          </div>
        </section>
      ) : null}

      {showMediaSection ? (
        <section className="mx-auto max-w-8xl px-4 pb-16 sm:px-6 lg:px-8">
          {topicMedia.loading ? (
            <div
              className="flex min-h-[12rem] items-center justify-center rounded-[2rem] border border-black/[0.08] bg-white"
              role="status"
              aria-busy="true"
            >
              <Loader2 className="h-8 w-8 animate-spin" style={{ color }} aria-hidden />
            </div>
          ) : (
            <div
              className="flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-black/[0.08] bg-white p-6 sm:p-8"
              style={{ borderColor: `${color}35` }}
            >
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-gray-400">
                  Watch &amp; listen
                </p>
                <h2 className="mt-2 text-2xl font-black text-gray-950">
                  {topicMedia.totalCount} video{topicMedia.totalCount === 1 ? "" : "s"} &amp; podcast
                  {topicMedia.totalCount === 1 ? "" : "s"} for{" "}
                  {topicMedia.topicIds?.subCategoryName || topicLabel}
                </h2>
                <p className="mt-2 text-sm text-gray-500">
                  Provider media matched to this subcategory in your interests.
                </p>
              </div>
              <Link
                to={`/${industryId}/${slug}`}
                state={{ activeTab: "media" }}
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-black text-black"
                style={{ background: color }}
              >
                Open media library
                <ArrowRight size={14} aria-hidden />
              </Link>
            </div>
          )}
        </section>
      ) : null}

      <ArticleAuthFlowModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onAuthenticated={() => setAuthModalOpen(false)}
        openWith={authIntent}
        articleTitle={article.title}
        sponsorName={sponsor.name}
        returnPath={returnPath}
        articleInterestCategory={industry.label}
        articleInterestSubCategory={topicLabel}
      />

      <SubcategoryInterestGateModal
        open={interestGate.open}
        color={color}
        industryLabel={interestGate.industryLabel}
        topicLabel={interestGate.topicLabel}
        onProceed={interestGate.onProceed}
        onGoBack={interestGate.onGoBack}
        pending={interestGate.pending}
      />

      <Footer />
    </div>
  );
};

export default ArticleDetailPage;
